import api from './apiClient';

export interface IncidentReport {
  _id: string;
  code: string;
  company: { _id: string; name: string } | string;
  description: string;
  occurredAt: string;
  reportedBy: { _id: string; firstName: string; lastName: string; email: string } | string;
  peopleInvolved: Array<{
    // Optional — a person involved doesn't have to be a registered User
    // (e.g. a contractor or bystander on site). Mirrors GuestVisit's
    // optional `registeredUser`.
    user?: { _id: string; firstName: string; lastName: string; email: string } | string;
    name: string;
    role?: string;
  }>;
  images: string[];
  status: 'open' | 'reviewed' | 'closed';
  createdAt: string;
  updatedAt: string;
}

interface CreateIncidentData {
  description: string;
  occurredAt: string;
  companyId?: string;
  peopleInvolved?: Array<{ user?: string; name: string; role?: string }>;
  images?: File[];
}

interface UpdateIncidentData {
  description?: string;
  occurredAt?: string;
  peopleInvolved?: Array<{ user?: string; name: string; role?: string }>;
  keepImages?: string[];
  images?: File[];
}

class IncidentReportApi {
  async create(data: CreateIncidentData): Promise<IncidentReport> {
    const formData = new FormData();
    formData.append('description', data.description);
    formData.append('occurredAt', data.occurredAt);

    if (data.companyId) {
      formData.append('companyId', data.companyId);
    }

    if (data.peopleInvolved) {
      formData.append('peopleInvolved', JSON.stringify(data.peopleInvolved));
    }

    if (data.images) {
      for (const image of data.images) {
        formData.append('images', image);
      }
    }

    const result = await api.post('/incident-reports', formData);
    return result.data;
  }

  async getAll(params?: {
    page?: number;
    limit?: number;
    status?: string;
    company?: string;
  }): Promise<{ data: IncidentReport[]; pagination: any }> {
    const queryParams: Record<string, any> = {};
    if (params?.page) queryParams.page = String(params.page);
    if (params?.limit) queryParams.limit = String(params.limit);
    if (params?.status) queryParams.status = params.status;
    if (params?.company) queryParams.company = params.company;

    return await api.get('/incident-reports', queryParams);
  }

  async getById(id: string): Promise<IncidentReport> {
    const result = await api.get(`/incident-reports/${id}`);
    return result.data;
  }

  async update(id: string, data: UpdateIncidentData): Promise<IncidentReport> {
    const formData = new FormData();
    if (data.description !== undefined) formData.append('description', data.description);
    if (data.occurredAt !== undefined) formData.append('occurredAt', data.occurredAt);
    if (data.peopleInvolved !== undefined) formData.append('peopleInvolved', JSON.stringify(data.peopleInvolved));
    if (data.keepImages !== undefined) formData.append('keepImages', JSON.stringify(data.keepImages));
    if (data.images) {
      for (const image of data.images) {
        formData.append('images', image);
      }
    }

    const result = await api.put(`/incident-reports/${id}`, formData);
    return result.data;
  }

  async getImageUrl(id: string, index: number): Promise<{ url: string; originalName?: string; contentType?: string }> {
    const result = await api.get(`/incident-reports/${id}/images/${index}`);
    return result.data;
  }

  async updateStatus(id: string, status: string): Promise<IncidentReport> {
    const result = await api.patch(`/incident-reports/${id}/status`, { status });
    return result.data;
  }

  async delete(id: string): Promise<void> {
    await api.delete(`/incident-reports/${id}`);
  }

  async getStats(params?: { company?: string }): Promise<{ open: number; reviewed: number; closed: number }> {
    const queryParams: Record<string, any> = {};
    if (params?.company) queryParams.company = params.company;
    const result = await api.get('/incident-reports/stats', queryParams);
    return result.data;
  }

  async exportPdf(params?: { company?: string }): Promise<Blob> {
    const queryParams: Record<string, any> = {};
    if (params?.company) queryParams.company = params.company;

    return api.getBlob('/exports/incident-reports', queryParams);
  }
}

export const incidentReportApi = new IncidentReportApi();
